import { useState, useRef } from "react";
import { withTranslation, TFunction } from "react-i18next";
import { notification, Progress } from "antd";
import { Button } from "../../common/Button";
import {
  UploadContainer,
  UploadArea,
  FileInput,
  FileList,
  FileItem,
  UploadButton,
  UploadTitle,
  UploadDescription,
} from "./styles";

interface UploadFormProps {
  t: TFunction;
}

const UploadForm = ({ t }: UploadFormProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // All drag handlers remain unchanged
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList: FileList) => {
    const newFiles = Array.from(fileList);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      notification.warning({
        message: "No Files Selected",
        description: "Please select at least one file to upload.",
      });
      return;
    }

    setUploading(true);
    setProgress(0);
    
    // Upload logic remains unchanged
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const uploadProgressInterval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 10;
          if (newProgress >= 90) {
            clearInterval(uploadProgressInterval);
            return 90;
          }
          return newProgress;
        });
      }, 300);

      const response = await fetch("https://cloudflare-worker.hyangcr.workers.dev/api/upload-to-r2", {
        method: "POST",
        body: formData,
      });

      clearInterval(uploadProgressInterval);

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      setProgress(100);
      
      notification.success({
        message: "Upload Complete",
        description: "Your files have been uploaded successfully!",
      });
      
      setFiles([]);
    } catch (error) {
      console.error("Error uploading files:", error);
      notification.error({
        message: "Upload Failed",
        description: "There was a problem uploading your files. Please try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <UploadContainer id="upload">
      <UploadArea
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={triggerFileInput}
      >
        {/* Moved title and description inside the upload area */}
        <UploadTitle>{t("Help Us to Improve AI")}</UploadTitle>
        <UploadDescription>{t("Drag and drop your homework and note(.ipynb) here, or click to select files")}</UploadDescription>
        
        <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 7V17M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z" stroke="currentColor" strokeWidth="2"/>
        </svg>
        <FileInput
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
        />
      </UploadArea>

      {files.length > 0 && (
        <>
          <FileList>
            {files.map((file, index) => (
              <FileItem key={index}>
                <span>{file.name}</span>
                <span>{(file.size / 1024).toFixed(1)} KB</span>
                <button onClick={() => handleRemoveFile(index)}>✕</button>
              </FileItem>
            ))}
          </FileList>

          {uploading && <Progress percent={progress} status={progress === 100 ? "success" : "active"} />}

          <UploadButton>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? t("Uploading...") : t("Upload")}
            </Button>
          </UploadButton>
        </>
      )}
    </UploadContainer>
  );
};

export default withTranslation()(UploadForm);